import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useState, useEffect, useCallback, useRef } from 'react';
// components
import SearchHeader from 'components/organisms/headers/Search';
import Category from 'components/organisms/main/Category';
import ClassRowList from 'components/organisms/main/ClassRowList';
import ClassGridList from 'components/organisms/main/ClassGridList';
import ClassFilter from 'components/molecules/common/ClassFilter';
import EmptyContent from 'components/molecules/common/EmptyContent';
import SpinLoading from 'components/atoms/common/SpinLoading';
import Layout from 'components/layout';
import MainLayout from 'cds/layout/Main';
import VerticalScrollLayout from 'cds/layout/VerticalScroll';
// slices
import { setClassList } from 'slices/search';
import { postClassSearch } from 'slices/search.thunk';
// hooks
import useMetaData from 'hooks/common/useMetaData';
import { useSelector, useDispatch } from 'hooks/common/useStore';
import useInfiniteScroll from 'hooks/common/useInfiniteScroll';
import useClassFilter from 'hooks/domain/class/useFilter';
// styles
import * as typography from 'cds/typography';
import { mediaQuery, mediaValue, palette } from 'cds/styles';
import { setClickableInformation } from 'slices/dataMining';

const orderMap = (order, sort) => {
  if (sort === 'signup_cnt' && order === 'desc') {
    return 'helpful_cnt';
  }
  if (sort === 'review_score' && order === 'desc') {
    return 'review_score';
  }
  if (sort === 'review_count' && order === 'desc') {
    return 'review_count';
  }
  if (sort === 'payment' && order === 'asc') {
    return 'payment';
  }
  if (sort === 'payment' && order === 'desc') {
    return 'payment_desc';
  }
  if (sort === 'class_id' && order === 'desc') {
    return 'class_id';
  }
};

/**
 * 검색 페이지
 */
const SearchPage = () => {
  const CALL_LIMIT = 20;

  const router = useRouter();
  const { keyword } = router.query;

  const dispatch = useDispatch();
  const totalCount = useSelector(state => state.search.info.totalCount);
  const classList = useSelector(state => state.search.classList);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const enableMoreData = useRef(true);
  const postCount = useRef(0);
  const target = useRef(null);

  useEffect(() => {
    dispatch(
      setClickableInformation({ [`검색어 : ${keyword ? keyword : '검색어 없음'}`]: classList }),
    );
  }, [classList, keyword, dispatch]);

  const { MetaTitle } = useMetaData();
  const { currentFilter, FilterDropdown, FilterSheet, onFilterShow } = useClassFilter([
    6, 0, 1, 3, 4, 5,
  ]);
  useInfiniteScroll({
    target,
    onIntersect: ([entry]) => {
      if (entry.isIntersecting) {
        onLoadData();
      }
    },
  });

  const onLoadData = useCallback(async () => {
    if (!enableMoreData.current || (!keyword && keyword !== '')) {
      return;
    }

    setIsLoading(true);
    enableMoreData.current = false;

    const res = await dispatch(
      postClassSearch({
        keyword,
        sort: orderMap(currentFilter.order, currentFilter.sort),
        start: postCount.current,
        end: postCount.current + CALL_LIMIT,
      }),
    ).catch(() => {});

    setIsLoading(false);
    setIsLoaded(true);
    if (res.payload && res.payload.length) {
      postCount.current += CALL_LIMIT + 1;
      enableMoreData.current = true;
    }
  }, [dispatch, keyword, currentFilter.sort, currentFilter.order]);

  useEffect(() => {
    dispatch(setClassList([]));
    setIsLoaded(false);
    enableMoreData.current = true;
    postCount.current = 0;
    onLoadData();
  }, [dispatch, onLoadData, keyword]);

  return (
    <>
      <MetaTitle content={`클래스 검색 ${keyword ? `- ${keyword}` : ''}`} />

      <Wrapper>
        <ContentLayout>
          <ClassFilter
            classTotalCount={totalCount}
            sortName={currentFilter.name}
            onClickSort={onFilterShow}
          >
            <FilterDropdown />
          </ClassFilter>

          <ClassWrapper>
            {classList.length > 0 && <ClassRowList list={classList} showSalePer showPrice />}
            {classList.length > 0 && (
              <ClassGridList
                list={classList}
                showSalePer
                showPrice
                slidePerView={[
                  {
                    break: 'default',
                    view: 2,
                  },
                  {
                    break: mediaValue.medium,
                    view: 2,
                  },
                  {
                    break: mediaValue.xlarge,
                    view: 3,
                  },
                  {
                    break: 1600,
                    view: 4,
                  },
                ]}
              />
            )}
            <div ref={target} />
          </ClassWrapper>

          {classList.length === 0 && isLoaded && (
            <>
              <EmptyLayout>
                <EmptyContent icon="ic_search" width={48} height={48} fill={palette.grey050}>
                  <div>
                    <strong>{keyword?.length > 20 ? `${keyword.slice(20)}...` : keyword}</strong>
                    에 대한
                    <br />
                    검색 결과가 없어요
                    <br />
                    검색어를 다시 확인해주세요.
                  </div>
                </EmptyContent>
              </EmptyLayout>

              <Divide />
              <TitleSection>찾고 있는 클래스가 있나요?</TitleSection>
              <Category />
            </>
          )}

          {(isLoading || !isLoaded) && (
            <LoadingLayout>
              <SpinLoading />
            </LoadingLayout>
          )}
        </ContentLayout>
      </Wrapper>

      <FilterSheet />
    </>
  );
};

SearchPage.getLayout = function getLayout(page) {
  return <Layout mobileHeader={<SearchHeader />}>{page}</Layout>;
};

const Wrapper = styled(MainLayout)`
  width: 100%;

  ${mediaQuery.large} {
    width: 1800px;
    padding: 0 72px;
  }
`;

const ContentLayout = styled(VerticalScrollLayout)`
  ${mediaQuery.large} {
    padding: 32px 0 128px;
  }
`;

const ClassWrapper = styled.div`
  padding: 0 16px;

  ${mediaQuery.medium} {
    padding: 0;
    margin-top: 32px;
  }

  & > ul:first-of-type {
    ${mediaQuery.medium} {
      display: none;
    }
  }

  & > ul:last-of-type {
    display: none;
    ${mediaQuery.medium} {
      display: block;
    }
  }
`;

const Divide = styled.hr`
  height: 10px;
  margin: 8px 0;
  border-style: solid;
  border-width: 1px 0 0 0;
  border-color: ${palette.border.b1};
  background-color: ${palette.background.bg2};

  ${mediaQuery.large} {
    display: none;
  }
`;

const EmptyLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 380px;
  text-align: center;
  ${typography.body2_regular};

  & svg {
    margin-bottom: 24px;
  }

  & strong {
    display: inline;
    color: ${palette.blue070};
    font-weight: 500;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  height: 72px;
  padding: 0 16px;
  ${typography.subtitle3_medium};
`;

const LoadingLayout = styled.div`
  margin-top: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0 24px 0;
`;

export default SearchPage;
